/**
 * The reference lists the client form picks from.
 *
 * All but one of them are the backend's catalogs. The Goal field is the
 * exception - it picks from the practitioner's own active services, which is
 * a different endpoint and a different kind of id; see `fetchServiceGoals` at
 * the foot of the file.
 *
 * Every one of them answers with a bare array of `{ id, name }` - the same
 * objects that come back embedded in a client record - and sits behind the
 * same bearer token as the rest of the API, so each call fetches a fresh Clerk
 * token first: they are short-lived, and holding one in a module would go
 * stale between screens.
 *
 * Adding a list is a path in `API_ENDPOINTS.catalog`, a one-line getter here,
 * and an entry in `useClientLookups`.
 */

import { api, API_ENDPOINTS } from "../../../lib/api";
import type { GetToken } from "../../auth/types/auth.types";
import type { LookupOption } from "../types/clients.types";

/** Wire shape of a reference list, exactly as the backend sends it. */
type LookupResponse = { id: number; name: string }[];

/**
 * Reads one reference list. Shared by every getter below, since the endpoints
 * differ only in their path.
 *
 * Rows without a usable name are dropped rather than shown: an empty option is
 * a dead line in the list, and one the form could never validate.
 */
async function fetchLookup(
  path: string,
  getToken: GetToken,
  signal?: AbortSignal
): Promise<LookupOption[]> {
  const token = await getToken();
  if (!token) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error(`No active Clerk session - cannot load ${path}.`);
  }

  const response = await api.get<LookupResponse>(path, { token, signal });

  return (response ?? [])
    .filter((row) => typeof row?.name === "string" && row.name.trim())
    .map((row) => ({ id: row.id, name: row.name.trim() }));
}

/** Salutations for the Title field. */
export const fetchTitles = (getToken: GetToken, signal?: AbortSignal) =>
  fetchLookup(API_ENDPOINTS.catalog.titles, getToken, signal);

/** What a client is signing up to achieve, for the Goal field. */
export const fetchGoals = (getToken: GetToken, signal?: AbortSignal) =>
  fetchLookup(API_ENDPOINTS.catalog.goals, getToken, signal);

/** How a client eats, for the Food preference field. */
export const fetchDietaryPatterns = (
  getToken: GetToken,
  signal?: AbortSignal
) => fetchLookup(API_ENDPOINTS.catalog.dietaryPatterns, getToken, signal);

/** What a client reacts to, for the Food allergies field. */
export const fetchAllergens = (getToken: GetToken, signal?: AbortSignal) =>
  fetchLookup(API_ENDPOINTS.catalog.allergens, getToken, signal);

/** What a client likes to eat, for the Favorite cuisines field. */
export const fetchCuisines = (getToken: GetToken, signal?: AbortSignal) =>
  fetchLookup(API_ENDPOINTS.catalog.cuisines, getToken, signal);

/** How active a client is, for the Life style field. */
export const fetchLifeStyles = (getToken: GetToken, signal?: AbortSignal) =>
  fetchLookup(API_ENDPOINTS.catalog.lifeStyles, getToken, signal);

/** What a client is being treated for, for the Medical conditions field. */
export const fetchMedicalConditions = (
  getToken: GetToken,
  signal?: AbortSignal
) => fetchLookup(API_ENDPOINTS.catalog.medicalConditions, getToken, signal);

/**
 * Wire shape of a service, as far as a dropdown reads one. The endpoint sends
 * more - a description, the timestamps, the catalog goal behind a curated one
 * - and none of it reaches the field.
 */
type ServiceListResponse = { public_id: string; name: string }[];

/**
 * What the practice offers, for the client form's Goal field.
 *
 * Not `fetchGoals` above: what a client is signed up against is one of the
 * practitioner's own services now, so the names come from
 * `/api/v1/practitioner-services/` and the id each option carries is that
 * service's `public_id` rather than a catalog id.
 *
 * `?is_active=true`, unlike the My brand tab's own listing: a service that has
 * been taken down is not something to sign a new client up to, and the tab is
 * where one is put back up. See `fetchServices` in `brand/servicesService`,
 * which deliberately does not filter.
 *
 * The list is a bare array, not DRF's envelope - a practice offers a handful
 * of services, and the endpoint sends all of them.
 *
 * The Add enquiry form still picks from `fetchGoals`: `POST /inquiries/add/`
 * takes a catalog goal id, and that has not changed.
 */
export async function fetchServiceGoals(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<LookupOption[]> {
  const token = await getToken();
  if (!token) {
    throw new Error(
      `No active Clerk session - cannot load ${API_ENDPOINTS.services.list}.`
    );
  }

  const response = await api.get<ServiceListResponse>(
    API_ENDPOINTS.services.list,
    { token, query: { is_active: true }, signal }
  );

  // A body that is anything but an array is an empty list rather than a `.map`
  // of undefined halfway up the form. Rows without a name or an id are dropped
  // for the same reason the catalog's are: an option the form could offer but
  // never save is worse than one it does not offer.
  return (Array.isArray(response) ? response : [])
    .filter(
      (row) =>
        typeof row?.name === "string" && row.name.trim() && !!row.public_id
    )
    .map((row) => ({ id: row.public_id, name: row.name.trim() }));
}
